import { Helmet } from 'react-helmet-async';
import type { PublicSupplierProfileVO } from '../../../types/model/valueObject/account/PublicSupplierProfileVO';
import { getSchemaTypes } from '../../location/profile/LocationProfileSEO';
import { DEFAULT_IMAGE, useClaimHead } from '../../seo/HeadDefaults';

interface SupplierProfileSEOProps {
  profile: PublicSupplierProfileVO | null;
}

const SITE = 'https://secur.space';

export const supplierCanonicalUrl = (profile: Pick<PublicSupplierProfileVO, 'shortId' | 'slug'>): string =>
  `${SITE}/supplier/${profile.shortId}/${profile.slug}`;

/** Organization JSON-LD for a supplier, listing its yards as places it operates. */
export const buildSupplierStructuredData = (profile: PublicSupplierProfileVO) => {
  const url = supplierCanonicalUrl(profile);
  const locations = profile.locations || [];
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${url}#organization`,
    name: profile.companyName,
    url,
    logo: profile.logoUrl || undefined,
    description: profile.companyDescription || undefined,
    foundingDate: profile.yearFounded ? String(profile.yearFounded) : undefined,
    // Parent of every listed yard, so a location's StorageFacility and this
    // Organization resolve to the same graph.
    // Same type derivation as the location page, so the shared @id never
    // carries conflicting types across the two documents.
    subOrganization: locations.map(l => ({
      '@type': getSchemaTypes(l.equipmentTypes),
      '@id': `${SITE}/location-profile/${l.locationId}`,
      name: l.locationName,
      url: `${SITE}/location-profile/${l.locationId}`,
      address: { '@type': 'PostalAddress', addressLocality: l.city, addressRegion: l.state, addressCountry: 'US' },
    })),
  };
};

const SupplierProfileSEO = ({ profile }: SupplierProfileSEOProps) => {
  useClaimHead();
  const companyName = profile?.companyName || '';
  const description = profile?.companyDescription
    ? `${profile.companyDescription.substring(0, 155)}…`
    : `${companyName} — truck parking, trailer storage, and container yards on SecurSpace.`;
  const canonicalUrl = profile?.shortId ? supplierCanonicalUrl(profile) : `${SITE}/search`;
  const title = companyName
    ? `${companyName} — Truck Parking & Storage Locations | SecurSpace`
    : 'Supplier Profile | SecurSpace';
  const logoUrl = profile?.logoUrl || '';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={`${companyName}, truck parking, container storage, trailer parking, intermodal storage`} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SecurSpace" />
      <meta property="og:image" content={logoUrl || DEFAULT_IMAGE} />
      {profile && (
        <script type="application/ld+json">
          {JSON.stringify(buildSupplierStructuredData(profile))}
        </script>
      )}
    </Helmet>
  );
};

export default SupplierProfileSEO;
